import { useCallback, useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

function getBuiltSha(): string {
  return process.env.REACT_APP_BUILD_SHA || '';
}

function isVersionCheckEnabled(): boolean {
  const builtSha = getBuiltSha();
  return Boolean(builtSha) && builtSha !== 'dev';
}

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as VersionInfo;
    return data.buildSha || null;
  } catch {
    return null;
  }
}

export function useAppVersion(): {
  updateAvailable: boolean;
  dismissUpdate: () => void;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const checkVersion = useCallback(async () => {
    if (!isVersionCheckEnabled() || dismissed) {
      return;
    }

    const deployedSha = await fetchDeployedVersion();
    const builtSha = getBuiltSha();
    if (deployedSha && deployedSha !== builtSha) {
      setUpdateAvailable(true);
    }
  }, [dismissed]);

  useEffect(() => {
    if (!isVersionCheckEnabled()) {
      return undefined;
    }

    void checkVersion();

    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };

    const handleVersionStale = () => {
      if (!dismissed) {
        setUpdateAvailable(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('appVersionStale', handleVersionStale);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('appVersionStale', handleVersionStale);
    };
  }, [checkVersion, dismissed]);

  const dismissUpdate = useCallback(() => {
    setDismissed(true);
    setUpdateAvailable(false);
  }, []);

  return {
    updateAvailable: updateAvailable && !dismissed,
    dismissUpdate,
  };
}

export { getBuiltSha, isVersionCheckEnabled };

export interface VersionInfo {
  buildSha: string;
}
